import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAttemptReview } from "@/hooks/useExam";
import ExamReviewQuestion from "@/components/exam/ExamReview";

export default function ExamReviewPage() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const { data: review, isLoading, error } = useAttemptReview(attemptId || "");
  const [filter, setFilter] = useState<"all" | "correct" | "incorrect">("all");

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <svg className="animate-spin h-8 w-8 text-primary-600" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (error || !review) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <p className="text-danger-600">Failed to load review.</p>
      </div>
    );
  }

  const filteredAnswers = review.questions.filter((q) => {
    if (filter === "correct") return q.is_correct;
    if (filter === "incorrect") return !q.is_correct;
    return true;
  });

  const correctCount = review.questions.filter((q) => q.is_correct).length;
  const incorrectCount = review.questions.filter((q) => !q.is_correct).length;

  return (
    <div className="page-enter mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Exam Review</h1>
          <p className="mt-1 text-sm text-gray-500">
            Score: {review.attempt.score}/{review.attempt.total_questions}
          </p>
        </div>
        <Link
          to={`/attempts/${attemptId}/results`}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Back to Results
        </Link>
      </div>

      {/* Filter buttons */}
      <div className="flex items-center space-x-2 mb-6">
        <button
          onClick={() => setFilter("all")}
          className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
            filter === "all"
              ? "bg-primary-600 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          All ({review.questions.length})
        </button>
        <button
          onClick={() => setFilter("correct")}
          className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
            filter === "correct"
              ? "bg-success-600 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          Correct ({correctCount})
        </button>
        <button
          onClick={() => setFilter("incorrect")}
          className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
            filter === "incorrect"
              ? "bg-danger-600 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          Incorrect ({incorrectCount})
        </button>
      </div>

      <div className="space-y-6">
        {filteredAnswers.map((reviewAnswer) => (
          <ExamReviewQuestion
            key={reviewAnswer.question_id}
            reviewAnswer={reviewAnswer}
            questionNumber={
              review.questions.findIndex((q) => q.question_id === reviewAnswer.question_id) + 1
            }
            totalQuestions={review.questions.length}
          />
        ))}
      </div>

      {filteredAnswers.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No questions match the selected filter.</p>
        </div>
      )}
    </div>
  );
}
